import {
  albumCollectionByIdQuery,
  albumCollectionCreate,
} from '@/entities/album-collections'
import {
  $lastCreatedAuthor,
  type AuthorsQueryModelWithPagination,
  createAuthorsQueryModel,
} from '@/entities/authors'
import { type AuthorPreview } from '@/entities/authors'
import {
  type AlbumCollectionModel,
  type AuthorAlbumCollectionModel,
  type GetApiAuthorsQueryParams,
} from '@/shared/api'
import { combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

interface AlbumCollectionCommonInformationFormGateProps {
  initialAuthor?: AuthorPreview
}
export const AlbumCollectionCommonInformationFormGate =
  createGate<AlbumCollectionCommonInformationFormGateProps>()

const $albumCollectionCommonInformationFormAlbumCollectionData =
  createStore<AlbumCollectionModel | null>(null)
export const albumCollectionCommonInformationFormAlbumCollectionDataChanged =
  createEvent<AlbumCollectionModel | null>()
$albumCollectionCommonInformationFormAlbumCollectionData.on(
  albumCollectionCommonInformationFormAlbumCollectionDataChanged,
  (_, albumCollection) => albumCollection,
)

// Authors

export const $albumCollectionCommonInformationFormInitialAuthor = createStore<{
  id: string
  nickname: string
} | null>(null)
  .on(
    AlbumCollectionCommonInformationFormGate.open,
    (_, state) => state.initialAuthor ?? null,
  )
  .on(AlbumCollectionCommonInformationFormGate.close, () => null)

export const authorsAlbumCollectionCommonInformationFormAuthorsQueryModel =
  createAuthorsQueryModel({
    usePagination: true,
    accumulate: true,
  }) as AuthorsQueryModelWithPagination

const {
  availableSet: authorsAlbumCollectionCommonInformationFormAvailableSet,
  unavailableSet: authorsAlbumCollectionCommonInformationFormUnavailableSet,
} = authorsAlbumCollectionCommonInformationFormAuthorsQueryModel
export const {
  //   Query
  query: authorsAlbumCollectionCommonInformationFormQuery,
  //   Init
  filtersInit: authorsAlbumCollectionCommonInformationFormFiltersInit,
  // Pagination
  pageIncreased: authorsAlbumCollectionCommonInformationFormPageIncreased,
  pageSizeChanged: authorsAlbumCollectionCommonInformationFormPageSizeChanged,
  //   Search
  $search: $authorsAlbumCollectionCommonInformationFormSearch,
  searchChanged: authorsAlbumCollectionCommonInformationFormSearchChanged,
  searchReset: authorsAlbumCollectionCommonInformationFormSearchReset,
  //   Refresh
  refreshed: authorsAlbumCollectionCommonInformationFormRefreshed,
  // Accumulated
  $accumulatedData:
    $authorsAlbumCollectionCommonInformationFormAccumulatedQueryData,
} = authorsAlbumCollectionCommonInformationFormAuthorsQueryModel

sample({
  clock: AlbumCollectionCommonInformationFormGate.open,
  target: authorsAlbumCollectionCommonInformationFormAvailableSet,
})
sample({
  clock: AlbumCollectionCommonInformationFormGate.close,
  target: authorsAlbumCollectionCommonInformationFormUnavailableSet,
})

sample({
  source: AlbumCollectionCommonInformationFormGate.state,
  clock: authorsAlbumCollectionCommonInformationFormAvailableSet,
  target: authorsAlbumCollectionCommonInformationFormRefreshed,
})

export const albumCollectionsAuthorsLoaded = createEvent<{
  authorIds: string[]
}>()

sample({
  clock: albumCollectionsAuthorsLoaded,
  fn: ({ authorIds }): Partial<GetApiAuthorsQueryParams> => ({
    AuthorIds: authorIds,
  }),
  target: authorsAlbumCollectionCommonInformationFormFiltersInit,
})

export const $albumCollectionCommonInformationFormAuthors = combine(
  $authorsAlbumCollectionCommonInformationFormAccumulatedQueryData,
  $albumCollectionCommonInformationFormAlbumCollectionData,
  $lastCreatedAuthor,
  $albumCollectionCommonInformationFormInitialAuthor,
  (
    fetchedCoauthors,
    albumCollection,
    lastCreatedAuthor,
    initialAuthor,
  ): AuthorAlbumCollectionModel[] => {
    const combined = [
      ...fetchedCoauthors.map((a) => ({
        id: a.id,
        nickname: a.nickname,
        isCoauthor: false,
      })),
      ...(albumCollection
        ? albumCollection.authors.filter((a) => !a.isCoauthor)
        : []),
      ...(lastCreatedAuthor
        ? [
            {
              id: lastCreatedAuthor.id,
              nickname: lastCreatedAuthor.nickname,
              isCoauthor: false,
            },
          ]
        : []),
      ...(initialAuthor ? [{ ...initialAuthor, isCoauthor: false }] : []),
    ]

    const uniqueMap = new Map<string, AuthorAlbumCollectionModel>()
    for (const author of combined) {
      uniqueMap.set(author.id, author)
    }

    return Array.from(uniqueMap.values())
  },
)

// Coauthors

export const coauthorsAlbumCollectionCommonInformationFormAuthorsQueryModel =
  createAuthorsQueryModel({
    usePagination: true,
    accumulate: true,
  }) as AuthorsQueryModelWithPagination
const {
  availableSet: coauthorsAlbumCollectionCommonInformationFormAvailableSet,
  unavailableSet: coauthorsAlbumCollectionCommonInformationFormUnavailableSet,
} = coauthorsAlbumCollectionCommonInformationFormAuthorsQueryModel
export const {
  //   Query
  query: coauthorsAlbumCollectionCommonInformationFormQuery,
  //   Init
  filtersInit: coauthorsAlbumCollectionCommonInformationFormFiltersInit,
  // Pagination
  pageIncreased: coauthorsAlbumCollectionCommonInformationFormPageIncreased,
  pageSizeChanged: coauthorsAlbumCollectionCommonInformationFormPageSizeChanged,
  //   Search
  $search: $coauthorsAlbumCollectionCommonInformationFormSearch,
  searchChanged: coauthorsAlbumCollectionCommonInformationFormSearchChanged,
  searchReset: coauthorsAlbumCollectionCommonInformationFormSearchReset,
  //   Refresh
  refreshed: coauthorsAlbumCollectionCommonInformationFormRefreshed,
  // Accumulated
  $accumulatedData:
    $coauthorsAlbumCollectionCommonInformationFormAccumulatedQueryData,
} = coauthorsAlbumCollectionCommonInformationFormAuthorsQueryModel

sample({
  clock: AlbumCollectionCommonInformationFormGate.open,
  target: coauthorsAlbumCollectionCommonInformationFormAvailableSet,
})
sample({
  clock: AlbumCollectionCommonInformationFormGate.close,
  target: coauthorsAlbumCollectionCommonInformationFormUnavailableSet,
})
sample({
  clock: coauthorsAlbumCollectionCommonInformationFormAvailableSet,
  target: coauthorsAlbumCollectionCommonInformationFormRefreshed,
})

export const $albumCollectionCommonInformationFormCoauthors = combine(
  $coauthorsAlbumCollectionCommonInformationFormAccumulatedQueryData,
  $albumCollectionCommonInformationFormAlbumCollectionData,
  $lastCreatedAuthor,
  (
    fetchedCoauthors,
    albumCollection,
    lastCreatedAuthor,
  ): AuthorAlbumCollectionModel[] => {
    const combined = [
      ...fetchedCoauthors.map((a) => ({
        id: a.id,
        nickname: a.nickname,
        isCoauthor: false,
      })),
      ...(albumCollection
        ? albumCollection.authors.filter((a) => a.isCoauthor)
        : []),
      ...(lastCreatedAuthor
        ? [
            {
              id: lastCreatedAuthor.id,
              nickname: lastCreatedAuthor.nickname,
              isCoauthor: false,
            },
          ]
        : []),
    ]

    const uniqueMap = new Map<string, AuthorAlbumCollectionModel>()
    for (const author of combined) {
      uniqueMap.set(author.id, author)
    }

    return Array.from(uniqueMap.values())
  },
)

// Form Query Data Combine
sample({
  clock: AlbumCollectionCommonInformationFormGate.open,
  fn: () => 10,
  target: [
    authorsAlbumCollectionCommonInformationFormPageSizeChanged,
    coauthorsAlbumCollectionCommonInformationFormPageSizeChanged,
  ],
})

// Check for form data load

const $formFieldsQueryData = combine(
  [
    authorsAlbumCollectionCommonInformationFormQuery.$data,
    coauthorsAlbumCollectionCommonInformationFormQuery.$data,
  ],
  (data) => data,
)

const $formFieldsQueryStatuses = combine(
  [
    authorsAlbumCollectionCommonInformationFormQuery.$status,
    coauthorsAlbumCollectionCommonInformationFormQuery.$status,
  ],
  (statuses) => statuses,
)

export const $isAlbumCollectionCommonInformationFormDataPending = combine(
  albumCollectionByIdQuery.$data,
  albumCollectionByIdQuery.$status,
  $formFieldsQueryData,
  $formFieldsQueryStatuses,
  (albumByIdQueryData, albumByIdQueryStatus, otherData, otherStatuses) => {
    if (!albumByIdQueryData && albumByIdQueryStatus === 'initial') return true
    return otherData.some((data, i) => {
      if (otherStatuses[i] === 'initial') return false
      return !data
    })
  },
)

//
export const lastCreatedAlbumCollectionIdReset = createEvent()
export const $lastCreatedAlbumCollectionId = createStore<string | null>(null)
  .on(albumCollectionCreate.finished.success, (_, data) => data.result.id)
  .on(lastCreatedAlbumCollectionIdReset, () => null)

sample({
  clock: albumCollectionCreate.finished.success,
  target: [albumCollectionCreate.reset],
})
